import { useI18n } from "../../lib/i18n";
import { useAuth } from "../../lib/auth";
import { Settings } from "lucide-react";
import { ACTIVITY_LABELS, api } from "../../lib/api";
import { useLocation, useSearch } from "wouter";
import { useConsentGate } from "../../lib/useConsentGate";
import { useDashboardState } from "./useDashboardState";
import { useEffect } from "react";
import { nav, NAV_VIEWS, NAV_LINKS, ALL_ACTIVITIES } from "./model";
import { type DashboardListing, listingCreatedAt } from "../../components/DashboardListingTable";
import { type DashboardProfileSection } from "../../components/DashboardOverview";
import { useDashboardRefresh } from "./useDashboardRefresh";

export function useDashboardController() {
  const { t } = useI18n();
  const { user, loading, setUser, logout } = useAuth();
  const navLabelKeys: Record<string, string> = {
    "My Items": "dashboard.nav.myItems", "My Services": "dashboard.nav.myServices", "My Company": "dashboard.nav.myCompany", "My Jobs": "dashboard.nav.myJobs",
    Hiring: "dashboard.nav.hiring", "My Projects": "dashboard.nav.myProjects", "My Bids": "dashboard.nav.myBids",
    "My Applications": "dashboard.nav.myApplications", Messages: "dashboard.nav.messages", Contracts: "dashboard.nav.contracts",
    "Wallet / Escrow": "dashboard.nav.wallet", "Saved Items": "dashboard.nav.saved", Settings: "dashboard.nav.settings",
  };
  const navLabel = (label: string) => navLabelKeys[label] ? t(navLabelKeys[label]) : label;
  const activityKeys: Record<string, string> = {
    find_work: "dashboard.activity.findWork", offer_service: "dashboard.activity.offerService",
    promote_company: "dashboard.activity.promoteCompany", hire: "dashboard.activity.hire",
    post_job: "dashboard.activity.postJob", post_project: "dashboard.activity.postProject",
  };
  const activityLabel = (activity: string) => activityKeys[activity] ? t(activityKeys[activity]) : ACTIVITY_LABELS[activity] || activity;
  const [, navigate] = useLocation();
  useConsentGate();
  const search = new URLSearchParams(useSearch());
  const view = search.get("view") || (search.get("tab") === "items" ? "items" : "");
  const dashboardState = useDashboardState(user);
  const { profileSection, setProfileSection, mobileTab, setMobileTab, isMobileViewport, setIsMobileViewport, mine, setMine, mineScope, setMineScope, mineError, setMineError, mineAttempt, setMineAttempt, myBids, setMyBids, showAllBids, setShowAllBids, showAllQuotes, setShowAllQuotes, myQuotes, setMyQuotes, myItems, setMyItems, showAllMyItems, setShowAllMyItems, orders, setOrders, orderBusy, setOrderBusy, pendingRevs, setPendingRevs, myRevs, setMyRevs, reviewScope, setReviewScope, reviewsFailed, setReviewsFailed, reviewDeal, setReviewDeal, showAllPendingReviews, setShowAllPendingReviews, showAllPurchases, setShowAllPurchases, showAllReceivedReviews, setShowAllReceivedReviews, wprofile, setWprofile, disputes, setDisputes, disputeDeal, setDisputeDeal, companies, setCompanies, profile, setProfile, activeResume, setActiveResume, engagements, setEngagements, showAllEngagements, setShowAllEngagements, showAllDisputes, setShowAllDisputes, creditRead, setCreditRead, repayAmt, setRepayAmt, repayBusy, setRepayBusy, repayErr, setRepayErr, listingBusy, setListingBusy, listingError, setListingError, confirmDel, setConfirmDel, undo, setUndo, boostTarget, setBoostTarget, boosts, setBoosts, paidPlansAvailable, setPaidPlansAvailable, listingQuery, setListingQuery, listingKind, setListingKind, listingStatus, setListingStatus, showAllListings, setShowAllListings, openListingActions, setOpenListingActions, scope, credit } = dashboardState;

  // Preserve the original phone boundary; desktop categories have independent state.
  useEffect(() => {
    const viewport = window.matchMedia("(max-width: 639px)");
    const updateViewport = () => setIsMobileViewport(viewport.matches);
    viewport.addEventListener("change", updateViewport);
    return () => viewport.removeEventListener("change", updateViewport);
  }, []);
  useEffect(() => { setMobileTab("listings"); }, [view]);
  useEffect(() => {
    setProfileSection("overview");
    setShowAllEngagements(false); setShowAllDisputes(false); setShowAllMyItems(false);
  }, [view, scope]);
  useEffect(() => { if (!loading && !user) navigate("/login"); }, [loading, user, navigate]);
  // v3.9.51 — super admins don't use the member dashboard; send them to Admin.
  useEffect(() => { if (!loading && user?.userType === "admin") navigate("/admin"); }, [loading, user, navigate]);
  const dashboardRefresh = useDashboardRefresh(dashboardState, user, view);
  const loadBoosts = () => { void dashboardRefresh.reloadGroup("boosts"); };
  const loadProfile = () => { void dashboardRefresh.reloadGroup("profile"); };
  // Auto-dismiss the "archived — undo" toast after a few seconds.
  useEffect(() => { if (!undo) return; const t = setTimeout(() => setUndo(null), 7000); return () => clearTimeout(t); }, [undo]);
  if (loading || !user) return { screen: "loading" as const };
  // v3.9.51 — admin is being redirected to /admin; don't flash the member dashboard.
  if (user.userType === "admin") return { screen: "admin" as const };

  const caps = user.capabilities || [];
  const accountNav = nav.filter(([label]) => {
    if (label === "My Applications") return caps.includes("find_work");
    if (label === "Hiring") return caps.some((capability) => capability === "hire" || capability === "post_job");
    return true;
  });
  const mobileAccountLinks = accountNav.map(([label]) => {
    const viewKey = NAV_VIEWS[label];
    return { label, fullLabel: navLabel(label), href: NAV_LINKS[label] || (viewKey ? `/dashboard?view=${viewKey}` : "/dashboard") };
  });
  const accountSection = accountNav.find(([label]) => NAV_VIEWS[label] === view);
  const missing = ALL_ACTIVITIES.filter((a) => !caps.includes(a));

  const overview = [
    { n: mine?.jobs.length ?? 0, l: t("dashboard.overview.jobs") },
    { n: mine?.projects.length ?? 0, l: t("dashboard.overview.projects") },
    { n: (mine?.rfqs?.length ?? 0), l: t("dashboard.overview.rfqs") },
    { n: mine?.services.length ?? 0, l: t("dashboard.overview.services") },
    { n: mine?.people.length ?? 0, l: t("dashboard.overview.resumes") },
  ];
  // [posting.close](../../../../docs/product/marketplace.md#posting-close): Services keep lifecycle separate from moderation; Close on other
  // posts hides them while retaining awards and history for owner management.
  const listings: DashboardListing[] = mineScope === scope && mine
    ? [
        ...mine.jobs.map((x) => ({ type: "job" as const, kind: "Job", title: x.title, tab: "Jobs", id: x.id, createdAt: listingCreatedAt(x), ownerUserId: x.ownerUserId, ownerClosed: x.ownerClosed, moderationHidden: x.moderationHidden, hidden: !!x.hidden, status: x.status })),
        ...mine.projects.map((x) => ({ type: "project" as const, kind: "Project", title: x.title, tab: "Project Bidding", id: x.id, createdAt: listingCreatedAt(x), ownerUserId: x.ownerUserId, ownerClosed: x.ownerClosed, moderationHidden: x.moderationHidden, hidden: !!x.hidden, status: x.status })),
        ...(mine.rfqs ?? []).map((x) => ({ type: "rfq" as const, kind: "RFQ", title: x.title, tab: "RFQs", id: x.id, createdAt: listingCreatedAt(x), ownerUserId: x.ownerUserId, ownerClosed: x.ownerClosed, moderationHidden: x.moderationHidden, hidden: !!x.hidden, status: x.status })),
        ...mine.services.map((x) => ({ type: "service" as const, kind: "Service", title: x.title, tab: "Services", id: x.id, createdAt: listingCreatedAt(x), ownerUserId: x.ownerUserId, ownerClosed: x.ownerClosed, moderationHidden: x.moderationHidden, hidden: !!x.hidden, status: x.status || "open" })),
        ...mine.companies.map((x) => ({ type: "company" as const, kind: "Company", title: x.name, tab: "Companies", id: x.id, createdAt: listingCreatedAt(x), ownerUserId: x.ownerUserId, ownerClosed: x.ownerClosed, moderationHidden: x.moderationHidden, hidden: !!x.hidden })),
        ...mine.people.map((x) => ({ type: "resume" as const, kind: "Résumé", title: x.title, tab: "Jobseekers", id: x.id, createdAt: listingCreatedAt(x), ownerUserId: x.ownerUserId, ownerClosed: x.ownerClosed, moderationHidden: x.moderationHidden, hidden: !!x.hidden })),
      ]
    : [];
  const query = listingQuery.trim().toLowerCase();
  const filteredListings = listings.filter((listing) => {
    const matchesQuery = !query || listing.title.toLowerCase().includes(query);
    const matchesKind = listingKind === "all" || listing.type === listingKind;
    const status = listing.ownerClosed || listing.status === "closed" ? "closed" : listing.type === "service" ? (listing.status === "open" ? (listing.hidden ? "hidden" : "live") : listing.status) : (listing.hidden ? "archived" : "live");
    const matchesStatus = listingStatus === "all" || listingStatus === status;
    return matchesQuery && matchesKind && matchesStatus;
  });

  // v3.9.48 — boost lookup. `resume` listings live under the people surface.
  const BOOSTABLE = new Set(["job", "project", "service", "company"]);
  const normBoost = (t: string) => (t === "resume" ? "people" : t);
  const boostedUntil = (type: string, id: string): Date | null => {
    const want = normBoost(type);
    const b = boosts.find((x) => x.postId === id && x.postType === want);
    return b ? new Date(b.until) : null;
  };

  async function enable(activity: string) {
    dashboardRefresh.cancel();
    const updated = await api.addActivity(activity);
    if (updated) setUser(updated);
  }
  async function repay() {
    dashboardRefresh.cancel();
    if (!isMobileViewport || !credit || repayBusy) return;
    setRepayErr(null);
    // Keep currency decimals intact instead of turning pasted 12.50 into 1250.
    const amount = Number(repayAmt.replace(/[₱,\s]/g, ""));
    if (!Number.isFinite(amount) || amount < 1) { setRepayErr("Enter an amount."); return; }
    // The existing credit API accepts whole pesos, including pasted 12.00.
    if (!Number.isInteger(amount)) { setRepayErr("Enter a whole-peso amount."); return; }
    setRepayBusy(true);
    try {
      const result = await api.repayCredit(amount);
      setCreditRead({ scope, data: { limit: result.limit, outstanding: result.outstanding, available: result.available } });
      setRepayAmt("");
    } catch (error: unknown) { setRepayErr(error instanceof Error ? error.message : "Could not repay."); }
    finally { setRepayBusy(false); }
  }
  async function doLogout() { await logout(); navigate("/"); }
  async function refreshOrders() { await dashboardRefresh.reloadGroup("orders"); }
  async function orderAction(id: string, action: "fulfill" | "confirm" | "cancel") {
    dashboardRefresh.cancel();
    setOrderBusy(id + action);
    try {
      if (action === "fulfill") await api.fulfillOrder(id);
      else if (action === "confirm") await api.confirmOrder(id);
      else await api.cancelOrder(id);
      await refreshOrders();
    } catch { /* surfaced via list state */ }
    finally { setOrderBusy(null); }
  }
  function openOrder(orderId: string) {
    navigate(`/orders/${encodeURIComponent(orderId)}`);
  }
  async function refreshDisputes() { await dashboardRefresh.reloadGroup("disputes"); }
  const pendingByDeal = new Map<string, any>(pendingRevs.map((p) => [p.dealId, p]));
  async function reviewDone() { setReviewDeal(null); await dashboardRefresh.reloadGroup("reviews"); }
  async function archiveItem(id: string) {
    dashboardRefresh.cancel();
    setOrderBusy(id + "archive");
    try { await api.archiveItem(id); await refreshOrders(); } catch { /* ignore */ }
    finally { setOrderBusy(null); }
  }
  async function restoreItemAction(id: string) {
    dashboardRefresh.cancel();
    setOrderBusy(id + "restore");
    try { await api.restoreItem(id); await refreshOrders(); } catch { /* ignore */ }
    finally { setOrderBusy(null); }
  }
  async function refreshMine() { await dashboardRefresh.reloadGroup("mine"); }
  async function archiveListing(type: string, id: string, title: string) {
    dashboardRefresh.cancel();
    setListingBusy(id + "archive"); setListingError(null);
    try { await api.archiveListing(type, id); await refreshMine(); setUndo({ type, id, title }); }
    catch (e) { setListingError(e instanceof Error ? e.message : "Could not archive the listing."); } finally { setListingBusy(null); }
  }
  async function restoreListing(type: string, id: string) {
    dashboardRefresh.cancel();
    setListingBusy(id + "restore"); setListingError(null);
    try { await api.restoreListing(type, id); await refreshMine(); setUndo(null); }
    catch (e) { setListingError(e instanceof Error ? e.message : "Could not restore the listing."); } finally { setListingBusy(null); }
  }
  async function deleteListing(type: string, id: string) {
    dashboardRefresh.cancel();
    setListingBusy(id + "delete"); setListingError(null);
    try {
      const deleted = await api.deleteListing(type, id);
      if (type === "company" && deleted.user) { setUser(deleted.user); navigate("/dashboard"); }
      else { await refreshMine(); setConfirmDel(null); setUndo(null); }
    }
    catch (e) { setListingError(e instanceof Error ? e.message : "Could not delete the listing."); } finally { setListingBusy(null); }
  }

  const profileTabs: { key: DashboardProfileSection; label: string }[] = [
    { key: "overview", label: t("dashboard.overview", "Overview") },
    { key: "listings", label: t("dashboard.listings.title") },
    { key: "activity", label: t("dashboard.activity", "Activity") },
    { key: "reviews", label: t("dashboard.reviews.title", "Reviews") },
  ];
  function selectProfileSection(section: DashboardProfileSection) {
    setProfileSection(section);
    setOpenListingActions(null);
  }
  return { screen: "ready" as const, ...dashboardState, dashboardRefresh, t, user, loading, setUser, logout, navLabelKeys, navLabel, activityKeys, activityLabel, navigate, view, loadBoosts, loadProfile, caps, accountNav, mobileAccountLinks, accountSection, missing, overview, listings, query, filteredListings, BOOSTABLE, normBoost, boostedUntil, enable, repay, doLogout, refreshOrders, orderAction, openOrder, refreshDisputes, pendingByDeal, reviewDone, archiveItem, restoreItemAction, refreshMine, archiveListing, restoreListing, deleteListing, profileTabs, selectProfileSection };
}

export type DashboardReady = Extract<ReturnType<typeof useDashboardController>, { screen: "ready" }>;
